import { uploadImageToS3 } from '@/utils/imageUtils';
import {
    partitionFilesBySignature,
    IMAGE_UPLOAD_ACCEPT
} from '@/components/shared/CFileInput/utils/fileSignatureValidation';

(function () {
    'ngInject';

    angular.module('app.shared.services').factory('MultipleFileUploadService', multipleFileUploadService);

    function multipleFileUploadService(
        $mdToast,
        TestImageService,
        DynamicVectorService,
        DynamicImageService,
        ImageUtilService,
        VectorUtilService,
        ImageAPIService,
        ProductService
    ) {
        /**
         * Validate a batch of user-selected files by their real signature (magic
         * bytes), not their extension. Mirrors CFileInput.updateModel: keep the
         * valid ones, skip the invalid ones, and notify once. Returns the files
         * that are safe to upload.
         */
        async function keepValidImageFiles(files) {
            const { valid, invalidCount } = await partitionFilesBySignature(files, IMAGE_UPLOAD_ACCEPT);
            if (invalidCount) {
                const message =
                    invalidCount === 1
                        ? 'This file is not a valid image (its real content does not match its extension) and was skipped. Please try another one.'
                        : `${invalidCount} files are not valid images (their real content does not match their extension) and were skipped. Please try others.`;
                $mdToast.show(
                    $mdToast
                        .simple()
                        .textContent(message)
                        .position('top right')
                        .hideDelay(4000)
                        .toastClass('md-toast-error')
                );
            }
            return valid;
        }
        async function uploadMultipleImages(
            $scope,
            canvas,
            files,
            object = null,
            isFromStep2 = false,
            isPlaceholder = false,
            firstStepPlaceholder = null
        ) {
            files = await keepValidImageFiles(files);
            if (!files.length) return;

            const { bulkEdit, imageSelected } = $scope;
            const targetObject = object ? object : imageSelected;
            const targetProperties = bulkEdit?.enabled ? bulkEdit.selectedProperties : $scope.selectedImageProperties;

            let position = targetProperties.dynamicImageVariation;
            let fileIndex = 1;
            const imageFiles = [];

            for (const file of files) {
                targetProperties.dynamicImageVariation = position;
                await TestImageService.previewImageOnPlaceholder(files[fileIndex - 1], canvas, targetObject.id, isFromStep2, isPlaceholder, firstStepPlaceholder).then(
                    async function () {
                        const isProductColors = targetObject.isColorOptions;
                        const provider = ProductService.getPod();

                        // If product-color or product-image element for template product/product bases
                        if (isProductColors) {
                            if (!['printify', 'printful'].includes(provider?.apiKeyReference)) {
                                imageFiles.push({
                                    optionId: position,
                                    name: file.name
                                });
                                await DynamicImageService.saveDynamicImageToPlaceholder(
                                    position,
                                    file,
                                    canvas,
                                    file.name,
                                    'product-bases',
                                    targetObject
                                );
                            } else {
                                await DynamicImageService.saveDynamicImageToPlaceholder(
                                    position,
                                    file,
                                    canvas,
                                    null,
                                    'product-bases',
                                    targetObject
                                );
                            }
                            if (ProductService.getPod()) {
                                ProductService.setIsProductColorModified(true);
                            }
                        } else {
                            if (targetObject.isOverlay) {
                                await DynamicImageService.saveDynamicImageToPlaceholder(
                                    position,
                                    file,
                                    canvas,
                                    null,
                                    'preview-mask',
                                    targetObject
                                );
                            } else {
                                await DynamicImageService.saveDynamicImageToPlaceholder(
                                    position,
                                    file,
                                    canvas,
                                    undefined,
                                    undefined,
                                    targetObject
                                );
                            }
                        }
                    }
                );
                position++;
                fileIndex++;
            }
            ProductService.setProductColorsFilenames(imageFiles);
        }

        async function uploadMultipleVectors(
            files,
            object,
            position,
            canvas,
            editMode,
            progressCallback,
            uploadStarted,
            uploadFinished,
            isSolidBg = false
        ) {
            let fileIndex = 0;
            for (const file of files) {
                if (uploadStarted) {
                    uploadStarted(position);
                }

                try {
                    // Upload the EPS file to the server
                    const vector = await DynamicVectorService.uploadEPSFile(
                        files[fileIndex],
                        progressCallback,
                        isSolidBg
                    );
                    // Add the vector into the object at the given position
                    DynamicVectorService.addEPSGroup(object, position, vector.SVG, vector.EPS, editMode);
                } catch (error) {
                    console.log(error);
                }

                if (uploadFinished) {
                    uploadFinished(position);
                }

                if (fileIndex < files.length) {
                    // Show the new uploaded EPS into the object
                    await DynamicVectorService.changeDynamicEPSPosition(object, position, canvas);
                    position++;
                    fileIndex++;
                }
            }
        }

        async function uploadFilesWithChunks(files, chunkSize, uploadFunction, progressCallback) {
            // Upload each File to the server
            let uploadedFiles = 0;
            let responses = [];

            // Initial progress
            progressCallback(0);

            for (let index = 0; index < files.length; index += chunkSize) {
                // Upload all the files in this chunk
                let promises = [];
                for (let chunk = 0; chunk < chunkSize; ++chunk) {
                    if (files[index + chunk]) {
                        // Upload file
                        promises.push(
                            uploadFunction(files[index + chunk])
                                .then((response) => {
                                    ++uploadedFiles;
                                    console.log(`${files[index + chunk].name} uploaded`);
                                    progressCallback((uploadedFiles / files.length) * 100);
                                    return response;
                                })
                                .catch((error) => {
                                    // Failed to upload
                                    console.error(error);
                                    ++uploadedFiles;
                                })
                        );
                    }
                }

                // Wait for all files to be uploaded
                responses.push.apply(responses, await Promise.all(promises));
            }

            progressCallback(100);
            return responses;
        }

        async function uploadVectorsWithChunks(files, chunkSize, generateThumbnail, progressCallback) {
            return await uploadFilesWithChunks(
                files,
                chunkSize,
                async function (file) {
                    return await VectorUtilService.uploadVectorToS3(file, 'vector', false, generateThumbnail);
                },
                progressCallback
            );
        }

        async function uploadDynamicImagesWithChunks(files, chunkSize, generateThumbnail, progressCallback) {
            files = await keepValidImageFiles(files);
            if (!files.length) return [];

            return await uploadFilesWithChunks(
                files,
                chunkSize,
                async function (file) {
                    let thumbnailPath = undefined;

                    const response = await uploadImageToS3('preview', file);
                    // Generate thumbnail if requested
                    if (generateThumbnail) {
                        const thumbResponse = await ImageAPIService.generateImageThumbnail(response.Name);
                        thumbnailPath = thumbResponse.Path;
                    }

                    return {
                        Path: response.Path,
                        ThumbnailPath: thumbnailPath,
                        OriginalName: file.name
                    };
                },
                progressCallback
            );
        }

        async function uploadLibraryCategoryImage(file, generateThumbnail, progressCallback) {
            return await uploadFilesWithChunks(
                [file],
                1,
                async function (file) {
                    let thumbnailPath = undefined;

                    const response = await uploadImageToS3('library-categories', file);
                    // Generate thumbnail if requested
                    if (generateThumbnail) {
                        const thumbResponse = await ImageAPIService.generateImageThumbnail(
                            response.Name,
                            'library-categories'
                        );
                        thumbnailPath = thumbResponse.Path;
                    }

                    return {
                        Path: response.Path,
                        ThumbnailPath: thumbnailPath
                    };
                },
                progressCallback
            );
        }

        return {
            uploadMultipleImages: uploadMultipleImages,
            uploadMultipleVectors: uploadMultipleVectors,
            uploadVectorsWithChunks: uploadVectorsWithChunks,
            uploadDynamicImagesWithChunks: uploadDynamicImagesWithChunks,
            uploadLibraryCategoryImage: uploadLibraryCategoryImage
        };
    }
})();
